'use client';

import { useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import DemoDataPill from './DemoDataPill';

// Card recipe shared by all six widgets in this row.
const cardStyle = {
  background: '#FFFFFF',
  boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
  border: '1px solid #F0F0F0',
};

export default function Row2_5NewBusinessAndCrew() {
  return (
    <>
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <LeadResponseTimer />
        <JobMarginHealth />
        <WeatherRescheduleAlerts />
      </section>
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <CrewCapacityHeatBar />
        <CrewProductivityLeaderboard />
        <CustomerSatisfactionPulse />
      </section>
    </>
  );
}

// ── 1. Lead Response Timer ─────────────────────────────────────────────────

interface Lead {
  id: string;
  name: string;
  city: string;
  createdAt: number;
}

// Red threshold is ≥15min — this is the canonical SLA threshold; count and color must agree.
const OVERDUE_MIN = 15;

function ageColor(min: number): { color: string; bg: string } {
  if (min < 5)            return { color: '#047857', bg: 'rgba(16,185,129,0.10)' };
  if (min < OVERDUE_MIN)  return { color: '#92400E', bg: 'rgba(245,158,11,0.10)' };
  return                         { color: '#B91C1C', bg: 'rgba(239,68,68,0.10)' };
}

function formatAge(min: number): string {
  const m = Math.floor(min);
  const s = Math.floor((min - m) * 60);
  return `${m}m ${s.toString().padStart(2, '0')}s`;
}

function LeadResponseTimer() {
  // Stable lead timestamps captured on mount; the count-up is driven by `now`.
  const [leads] = useState<Lead[]>(() => []);
  const [now, setNow] = useState<number>(() => Date.now());

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 10_000);
    return () => clearInterval(interval);
  }, []);

  const overdueCount = leads.filter((l) => (now - l.createdAt) / 60_000 >= OVERDUE_MIN).length;

  return (
    <div className="relative rounded-xl p-5" style={cardStyle}>
      <DemoDataPill />
      <div className="flex items-center gap-2 mb-1">
        <Icon name="BoltIcon" size={15} style={{ color: '#F59E0B' }} />
        <h3 className="text-base font-bold" style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}>
          Lead Response Timer
        </h3>
      </div>
      <p className="text-xs text-gray-400 mb-3">
        {leads.length} leads · {overdueCount} overdue (≥15min)
      </p>
      <div className="space-y-2">
        {leads.map((lead) => {
          const ageMin = (now - lead.createdAt) / 60_000;
          const { color, bg } = ageColor(ageMin);
          return (
            <div
              key={lead.id}
              className="flex items-center justify-between gap-3 rounded-lg px-3 py-2"
              style={{ border: '1px solid #F3F4F6' }}
            >
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-800 truncate">{lead.name}</p>
                <p className="text-[11px] text-gray-500 truncate">{lead.city}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span
                  className="rounded-full px-2 py-0.5 text-[11px] font-bold font-mono"
                  style={{ background: bg, color }}
                  suppressHydrationWarning
                >
                  {formatAge(ageMin)}
                </span>
                <button
                  className="rounded-md px-2 py-1 text-[11px] font-semibold text-white hover:opacity-90 active:scale-95 transition"
                  style={{ background: '#07B0E9' }}
                  suppressHydrationWarning
                >
                  Call
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── 2. Job Margin Health ───────────────────────────────────────────────────

interface MarginRow {
  jobId: string;
  customer: string;
  contractValue: number;
  consumedPct: number;
}

const MARGIN_ROWS: MarginRow[] = [];

function marginStatus(pct: number): { label: string; color: string; bg: string; bar: string } {
  if (pct > 95) return { label: 'Bleeding', color: '#B91C1C', bg: 'rgba(239,68,68,0.15)', bar: '#EF4444' };
  if (pct > 80) return { label: 'At Risk',  color: '#92400E', bg: 'rgba(245,158,11,0.15)', bar: '#F59E0B' };
  if (pct > 60) return { label: 'Watch',    color: '#92400E', bg: 'rgba(245,158,11,0.08)', bar: '#FBBF24' };
  return            { label: 'Healthy',  color: '#047857', bg: 'rgba(16,185,129,0.10)', bar: '#10B981' };
}

function JobMarginHealth() {
  const sorted = [...MARGIN_ROWS].sort((a, b) => b.consumedPct - a.consumedPct).slice(0, 5);
  const bleeding = MARGIN_ROWS.filter((r) => r.consumedPct > 95).length;
  const atRisk = MARGIN_ROWS.filter((r) => r.consumedPct > 80 && r.consumedPct <= 95).length;
  const flagSummary =
    bleeding > 0 && atRisk > 0 ? `${bleeding} bleeding · ${atRisk} at risk`
    : bleeding > 0             ? `${bleeding} bleeding`
    : atRisk > 0               ? `${atRisk} at risk`
    :                            '0 flagged';

  return (
    <div className="relative rounded-xl p-5" style={cardStyle}>
      <DemoDataPill />
      <div className="flex items-center gap-2 mb-1">
        <Icon name="ScaleIcon" size={15} style={{ color: '#EF4444' }} />
        <h3 className="text-base font-bold" style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}>
          Job Margin Health
        </h3>
      </div>
      <p className="text-xs text-gray-400 mb-3">
        {MARGIN_ROWS.length} active jobs · {flagSummary}
      </p>
      <div className="space-y-2">
        {sorted.map((r) => {
          const s = marginStatus(r.consumedPct);
          return (
            <div key={r.jobId} className="rounded-lg px-3 py-2" style={{ border: '1px solid #F3F4F6' }}>
              <div className="flex items-center justify-between gap-3 mb-1.5">
                <div className="min-w-0">
                  <p className="text-[10px] text-gray-500 font-mono">{r.jobId}</p>
                  <p className="text-sm font-semibold text-gray-800 truncate">
                    {r.customer} · ${r.contractValue.toLocaleString()}
                  </p>
                </div>
                <span
                  className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide flex-shrink-0"
                  style={{ background: s.bg, color: s.color }}
                >
                  {s.label}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-1.5 rounded-full" style={{ background: '#F3F4F6' }}>
                  <div
                    className="h-1.5 rounded-full"
                    style={{ width: `${r.consumedPct}%`, background: s.bar }}
                  />
                </div>
                <span className="text-[11px] font-bold text-gray-600 w-10 text-right">{r.consumedPct}%</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── 3. Weather Reschedule Alerts ───────────────────────────────────────────

interface WeatherAlert {
  jobId: string;
  city: string;
  tone: 'rain' | 'severe';
  text: string;
}

const WEATHER_ALERTS: WeatherAlert[] = [];

function WeatherRescheduleAlerts() {
  const toneStyles = (t: WeatherAlert['tone']) =>
    t === 'severe'
      ? { color: '#B91C1C', bg: 'rgba(239,68,68,0.10)',  icon: 'ExclamationTriangleIcon' as const }
      : { color: '#92400E', bg: 'rgba(245,158,11,0.10)', icon: 'CloudIcon' as const };

  return (
    <div className="relative rounded-xl p-5" style={cardStyle}>
      <DemoDataPill />
      <div className="flex items-center gap-2 mb-1">
        <Icon name="CloudIcon" size={15} style={{ color: '#F59E0B' }} />
        <h3 className="text-base font-bold" style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}>
          Weather Reschedule Alerts
        </h3>
      </div>
      <p className="text-xs text-gray-400 mb-3">Wind &gt;25mph or precip &gt;40% in next 24h</p>
      {WEATHER_ALERTS.length === 0 ? (
        <div
          className="flex items-center gap-2 rounded-lg p-3"
          style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.18)' }}
        >
          <Icon name="CheckCircleIcon" size={18} style={{ color: '#10B981' }} />
          <p className="text-xs font-medium" style={{ color: '#047857' }}>
            No weather risks in next 24h
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {WEATHER_ALERTS.map((a) => {
            const c = toneStyles(a.tone);
            return (
              <div
                key={a.jobId}
                className="flex items-center justify-between gap-3 rounded-lg px-3 py-2"
                style={{ border: '1px solid #F3F4F6' }}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: c.bg }}
                  >
                    <Icon name={c.icon} size={16} style={{ color: c.color }} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] text-gray-500 font-mono">{a.jobId} · {a.city}</p>
                    <p className="text-sm font-semibold text-gray-800 truncate">{a.text}</p>
                  </div>
                </div>
                <button
                  className="rounded-md px-2 py-1 text-[11px] font-semibold flex-shrink-0 hover:opacity-90 active:scale-95 transition"
                  style={{ background: 'rgba(7,176,233,0.10)', color: '#0369A1' }}
                  suppressHydrationWarning
                >
                  Reschedule
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── 4. Crew Capacity Heat Bar ──────────────────────────────────────────────

const CAPACITY_SEGMENTS: Array<{ label: string; hours: number; color: string }> = [];

function CrewCapacityHeatBar() {
  const total = CAPACITY_SEGMENTS.reduce((s, x) => s + x.hours, 0);
  const committed =
    (CAPACITY_SEGMENTS.find((s) => s.label === 'Committed')?.hours ?? 0) +
    (CAPACITY_SEGMENTS.find((s) => s.label === 'Overcommitted')?.hours ?? 0);
  const pct = (hours: number) => (total > 0 ? Math.round((hours / total) * 100) : 0);

  return (
    <div className="relative rounded-xl p-5" style={cardStyle}>
      <DemoDataPill />
      <div className="flex items-start justify-between gap-3 mb-3 pr-16">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Icon name="UserGroupIcon" size={15} style={{ color: '#033494' }} />
            <h3 className="text-base font-bold" style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}>
              Crew Capacity
            </h3>
          </div>
          <p className="text-xs text-gray-400 mt-0.5">Today · 0 crew members</p>
        </div>
        <p className="text-[11px] font-semibold text-gray-700 whitespace-nowrap mt-0.5">
          {committed} of {total}h committed
        </p>
      </div>
      <div className="flex h-5 rounded-full overflow-hidden" style={{ background: '#F3F4F6' }}>
        {CAPACITY_SEGMENTS.map((s) => (
          <div
            key={s.label}
            style={{ width: `${pct(s.hours)}%`, background: s.color }}
            title={`${s.label}: ${s.hours}h`}
          />
        ))}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
        {CAPACITY_SEGMENTS.map((s) => (
          <div key={s.label} className="rounded-lg p-2" style={{ border: '1px solid #F3F4F6' }}>
            <div className="flex items-center gap-1.5 mb-0.5">
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: s.color }} />
              <p className="text-[9px] uppercase tracking-wider font-semibold text-gray-500 truncate">{s.label}</p>
            </div>
            <p
              className="text-lg font-bold leading-tight"
              style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}
            >
              {s.hours}h
            </p>
            <p className="text-[10px] text-gray-500">{pct(s.hours)}%</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── 5. Crew Productivity Leaderboard ───────────────────────────────────────

interface CrewProductivity {
  name: string;
  sqPerHr: number;
}

const CREW_PRODUCTIVITY: CrewProductivity[] = [];

const PRODUCTIVITY_TARGET = 4.0;
const MEDAL_EMOJIS = ['🥇', '🥈', '🥉'];

function LeaderboardRow({ rankIcon, crew }: { rankIcon: string; crew: CrewProductivity }) {
  const delta = crew.sqPerHr - PRODUCTIVITY_TARGET;
  const positive = delta >= 0;
  return (
    <div
      className="flex items-center justify-between gap-2 rounded-lg px-2.5 py-2"
      style={{ border: '1px solid #F3F4F6' }}
    >
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-sm flex-shrink-0">{rankIcon}</span>
        <p className="text-xs font-semibold text-gray-800 truncate">{crew.name}</p>
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <span className="text-sm font-bold" style={{ color: '#000150' }}>
          {crew.sqPerHr.toFixed(1)}
        </span>
        <span
          className="text-[10px] font-bold"
          style={{ color: positive ? '#10B981' : '#EF4444' }}
        >
          {positive ? '▲' : '▼'} {Math.abs(delta).toFixed(1)}
        </span>
      </div>
    </div>
  );
}

function CrewProductivityLeaderboard() {
  const sorted = [...CREW_PRODUCTIVITY].sort((a, b) => b.sqPerHr - a.sqPerHr);
  const top = sorted.slice(0, 3);
  const bottom = sorted.slice(-3).reverse();
  const avg =
    CREW_PRODUCTIVITY.length > 0
      ? CREW_PRODUCTIVITY.reduce((s, c) => s + c.sqPerHr, 0) / CREW_PRODUCTIVITY.length
      : 0;

  return (
    <div className="relative rounded-xl p-5" style={cardStyle}>
      <DemoDataPill />
      <div className="flex items-center gap-2 mb-1">
        <Icon name="TrophyIcon" size={15} style={{ color: '#F59E0B' }} />
        <h3 className="text-base font-bold" style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}>
          Crew Productivity
        </h3>
      </div>
      <p className="text-xs text-gray-400 mb-3">
        WTD avg {avg.toFixed(1)} sq/hr (target {PRODUCTIVITY_TARGET.toFixed(1)})
      </p>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <p
            className="text-[10px] uppercase tracking-wider font-bold mb-1.5 flex items-center gap-1"
            style={{ color: '#047857' }}
          >
            <span>🏆</span> Top 3
          </p>
          <div className="space-y-1.5">
            {top.map((c, i) => (
              <LeaderboardRow key={c.name} rankIcon={MEDAL_EMOJIS[i]} crew={c} />
            ))}
          </div>
        </div>
        <div>
          <p
            className="text-[10px] uppercase tracking-wider font-bold mb-1.5 flex items-center gap-1"
            style={{ color: '#B91C1C' }}
          >
            <span>⚠</span> Bottom 3
          </p>
          <div className="space-y-1.5">
            {bottom.map((c) => (
              <LeaderboardRow key={c.name} rankIcon="⚠" crew={c} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── 6. Customer Satisfaction Pulse ─────────────────────────────────────────

interface Quote {
  name: string;
  text: string;
  positive: boolean;
}

const RECENT_QUOTES: Quote[] = [];

function initialsOf(full: string): string {
  return full
    .split(' ')
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

function CustomerSatisfactionPulse() {
  const score = 0;
  const delta = 0;
  const positive = delta >= 0;

  return (
    <div className="relative rounded-xl p-5" style={cardStyle}>
      <DemoDataPill />
      <div className="flex items-center gap-2 mb-3">
        <Icon name="HeartIcon" size={15} style={{ color: '#EF4444' }} />
        <h3 className="text-base font-bold" style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}>
          Reputation Score
        </h3>
      </div>
      <div className="flex items-baseline gap-3 mb-3">
        <p
          className="text-4xl font-bold leading-none"
          style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}
        >
          {score}
        </p>
        <div>
          <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-500">
            Rolling 7-day NPS
          </p>
          <p
            className="text-xs font-bold"
            style={{ color: positive ? '#10B981' : '#EF4444' }}
          >
            {positive ? '▲' : '▼'} {Math.abs(delta)} vs prior 7d
          </p>
        </div>
      </div>
      <div className="space-y-2">
        {RECENT_QUOTES.map((q) => (
          <div
            key={q.name}
            className="flex items-start gap-2 rounded-lg p-2.5"
            style={{ border: '1px solid #F3F4F6' }}
          >
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #07B0E9 0%, #033494 100%)' }}
            >
              {initialsOf(q.name)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 mb-0.5">
                <p className="text-xs font-semibold text-gray-800 truncate">{q.name}</p>
                <Icon
                  name={q.positive ? 'HandThumbUpIcon' : 'HandThumbDownIcon'}
                  size={12}
                  style={{ color: q.positive ? '#10B981' : '#EF4444' }}
                />
              </div>
              <p className="text-[11px] text-gray-600 leading-snug">&ldquo;{q.text}&rdquo;</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
